import { useLayoutEffect, useMemo, useRef } from 'react';
import { typography, radius, semantic } from '@/shared/theme/tokens';
import { LIVE_STATUS_META, withAlpha } from '../../liveModeMeta';
import { useFurnaceSvg } from '../../hooks/useFurnaceSvg';
import { Sparkline } from '../../ui/Sparkline';
import { MONO } from './recommendedTheme';

const STATUS_RANK = { critical: 0, warning: 1, ok: 2 };

// One callout per parameter, worst-status first, capped. Stale never floats live.
function pickCallouts(params, max = 3) {
  const byName = new Map();
  for (const p of params) {
    if (p.isStale) continue;
    const cur = byName.get(p.parameterName);
    if (!cur || STATUS_RANK[p.status] < STATUS_RANK[cur.status]) byName.set(p.parameterName, p);
  }
  return [...byName.values()].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]).slice(0, max);
}

/**
 * Furnace identity/anchor — the plant SVG rendered in FULL COLOUR (no recolour
 * filter), matching today's Live Mode furnace. Display-only (no SVG click
 * handlers, as today). Live value chips float on the right, coloured by status.
 * Theme-aware via `c`.
 */
export function FurnaceColored({ svgUrl, params = [], animate, c, isDark }) {
  const hostRef = useRef(null);
  const { data: markup, isLoading, isError } = useFurnaceSvg(svgUrl);
  const callouts = useMemo(() => pickCallouts(params), [params]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;
    host.innerHTML = markup;
    const svg = host.querySelector('svg');
    if (svg) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.maxHeight = '100%';
    }
  }, [markup]);

  return (
    <section style={{
      position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', borderBottom: `1px solid ${c.border}` }}>
        <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: c.textPrimary, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase' }}>
          Furnace
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: MONO, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.wide, color: c.textSubtle }}>
          <span style={{ width: 6, height: 6, borderRadius: radius.full, background: semantic.successAlt, animation: animate ? 'rec-pulse 1.8s ease-in-out infinite' : 'none' }} />
          LIVE SCHEMATIC
        </span>
      </div>

      <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* colored SVG backdrop */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 14, filter: isDark ? 'brightness(0.92) contrast(1.05)' : 'none',
        }}>
          {isLoading && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: c.textSubtle }}>LOADING SCHEMATIC…</span>}
          {isError && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 32, color: c.textSubtle }}>broken_image</span>
              <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: c.textSubtle }}>SCHEMATIC UNAVAILABLE</span>
            </div>
          )}
          {!isLoading && !isError && (
            <div ref={hostRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          )}
        </div>

        {/* healthy chip */}
        {!isLoading && !isError && callouts.length === 0 && (
          <div style={{
            position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 7,
            padding: '6px 10px', background: c.bg, border: `1px solid ${LIVE_STATUS_META.ok.border}`, borderRadius: radius.sm,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: radius.full, background: LIVE_STATUS_META.ok.color }} />
            <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide, color: c.textPrimary }}>ALL SYSTEMS NOMINAL</span>
          </div>
        )}

        {/* live value callouts — display-only */}
        <div style={{ position: 'absolute', top: 12, right: 12, bottom: 12, display: 'flex', flexDirection: 'column', gap: 8, width: 168, pointerEvents: 'none' }}>
          {callouts.map((p) => {
            const meta = LIVE_STATUS_META[p.status] ?? LIVE_STATUS_META.ok;
            return (
              <div key={p.id} style={{
                background: c.bg, border: `1px solid ${meta.border}`, borderLeft: `3px solid ${meta.color}`,
                borderRadius: radius.sm, padding: '7px 9px', boxShadow: isDark ? 'none' : `0 1px 4px ${withAlpha(meta.color, '1f')}`,
              }}>
                <div style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textMuted, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.parameterName}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                  <span style={{ fontFamily: MONO, fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, color: meta.color, fontVariantNumeric: 'tabular-nums' }}>{p.liveValue}</span>
                  {p.unit && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle }}>{p.unit}</span>}
                </div>
                <div style={{ marginTop: 3 }}><Sparkline data={p.buffer} color={meta.color} height={16} animate={false} /></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
